import React, { useState } from 'react';

import { Box, IconButton, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { FiX } from 'react-icons/fi';

import { PhoneListResult } from '../hooks/usePhoneListPaste';
import { getBackground, getColor } from '../theme/styles';

const VISIBLE_ROWS = 5;
const MAX_RAW_LENGTH = 24;

const useStyles = makeStyles((theme: Theme) => {
  const darkMode = theme.palette.type === 'dark';
  const colors = getColor(darkMode);

  return createStyles({
    root: {
      marginTop: 12,
    },
    summaryRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 8,
    },
    summary: {
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    added: {
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
    errorBlock: {
      marginTop: 8,
      padding: '10px 12px',
      borderRadius: 12,
      backgroundColor: getBackground(darkMode).errorBackgroundInput,
      border: `1px solid ${colors.error}`,
    },
    errorHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    errorTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: colors.error,
    },
    dismiss: {
      padding: 2,
      color: theme.palette.text.secondary,
    },
    rows: {
      marginTop: 6,
      maxHeight: 132,
      overflowY: 'auto',
    },
    row: {
      fontSize: 13,
      lineHeight: '20px',
      color: theme.palette.text.primary,
    },
    reason: {
      color: theme.palette.text.secondary,
    },
    more: {
      marginTop: 2,
      fontWeight: 600,
      fontSize: 13,
      color: theme.palette.primary.main,
      cursor: 'pointer',
      display: 'inline-block',
    },
  });
});

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

const truncate = (value: string) =>
  value.length > MAX_RAW_LENGTH ? `${value.slice(0, MAX_RAW_LENGTH)}…` : value;

interface Props {
  result: PhoneListResult;
  onDismiss: () => void;
  /** Wording for the cap, e.g. "you can add up to 500 per invite" */
  limitHint?: string;
}

/** What a pasted list of numbers turned into — added, skipped, and what couldn't be read. */
export const BulkPasteSummary: React.FC<Props> = ({ result, onDismiss, limitHint }) => {
  const classes = useStyles();
  const [showAll, setShowAll] = useState<boolean>(false);

  const { added, duplicates, skippedOverLimit, ignored, invalid } = result;
  const visible = showAll ? invalid : invalid.slice(0, VISIBLE_ROWS);
  const hidden = invalid.length - visible.length;

  return (
    <Box className={classes.root}>
      <Box className={classes.summaryRow}>
        <Typography className={classes.summary}>
          <span className={classes.added}>
            {added} {plural(added, 'number', 'numbers')} added.
          </span>
          {duplicates > 0 &&
            ` ${duplicates} ${plural(duplicates, 'was', 'were')} already in the list.`}
          {skippedOverLimit > 0 && ` ${skippedOverLimit} skipped${limitHint ? ` — ${limitHint}` : ''}.`}
          {ignored > 0 && ` ${ignored} ${plural(ignored, 'line', 'lines')} had no number.`}
          {result.usedDefaultCountry &&
            ` Numbers without a country code were added as ${result.defaultCountryName} (${result.defaultDialCode}).`}
        </Typography>

        <IconButton className={classes.dismiss} size="small" aria-label="Dismiss" onClick={onDismiss}>
          <FiX />
        </IconButton>
      </Box>

      {invalid.length > 0 && (
        <Box className={classes.errorBlock}>
          <Box className={classes.errorHeader}>
            <Typography className={classes.errorTitle}>
              {invalid.length} {plural(invalid.length, 'number', 'numbers')} couldn't be added
            </Typography>
          </Box>

          <Box className={classes.rows}>
            {visible.map((item, index) => (
              <Typography key={`${item.raw}-${index}`} className={classes.row}>
                {truncate(item.raw)} <span className={classes.reason}>— {item.reason}</span>
              </Typography>
            ))}
          </Box>

          {(hidden > 0 || showAll) && (
            <Typography className={classes.more} onClick={() => setShowAll(value => !value)}>
              {showAll ? 'less' : `+ ${hidden} more`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
